import React, { useMemo, useState } from 'react';

const formatCurrency = (amount, unit = 'USD') => {
  const value = Number.isFinite(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: unit || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  } catch (e) {
    return `${value.toFixed(2)} ${unit || ''}`.trim();
  }
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
};

const StatusBadge = ({ status }) => {
  const normalized = (status || '').toLowerCase();
  let classes = 'bg-gray-100 text-gray-700';
  if (['inservice', 'running'].includes(normalized)) {
    classes = 'bg-green-100 text-green-800';
  } else if (['stopped', 'failed', 'deleting', 'deleted'].includes(normalized)) {
    classes = 'bg-red-100 text-red-800';
  } else if (['pending', 'stopping', 'updating', 'creating', 'rollingback'].includes(normalized)) {
    classes = 'bg-yellow-100 text-yellow-800';
  }
  return (
    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${classes}`}>
      {status || 'unknown'}
    </span>
  );
};

const SageMakerTab = ({
  costs,
  notebooks,
  endpoints,
  apps,
  accounts,
  loading,
  error,
  actionInProgress,
  onRefresh,
  onStopNotebook,
  onDeleteEndpoint,
  onDeleteApp
}) => {
  const appList = apps || [];
  const [accountFilter, setAccountFilter] = useState('all');

  const maxCost = useMemo(() => {
    if (!costs || !costs.accounts || costs.accounts.length === 0) return 0;
    return Math.max(...costs.accounts.map((a) => a.amount || 0));
  }, [costs]);

  const filteredNotebooks = useMemo(() => {
    if (accountFilter === 'all') return notebooks;
    return notebooks.filter((nb) => nb.accountId === accountFilter);
  }, [notebooks, accountFilter]);

  const filteredEndpoints = useMemo(() => {
    if (accountFilter === 'all') return endpoints;
    return endpoints.filter((ep) => ep.accountId === accountFilter);
  }, [endpoints, accountFilter]);

  const filteredApps = useMemo(() => {
    if (accountFilter === 'all') return appList;
    return appList.filter((a) => a.accountId === accountFilter);
  }, [appList, accountFilter]);

  const runningNotebooks = notebooks.filter((nb) => (nb.status || '').toLowerCase() === 'inservice').length;
  const liveEndpoints = endpoints.filter((ep) => (ep.status || '').toLowerCase() === 'inservice').length;
  const liveApps = appList.filter((a) => (a.status || '').toLowerCase() === 'inservice').length;

  return (
    <div className="w-full px-0 sm:px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">SageMaker Control</h2>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded text-sm"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}

      {/* Cost dashboard */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Current Month SageMaker Spend</h3>
          {costs && costs.period && (
            <span className="text-sm text-gray-500">
              {costs.period.Start} to {costs.period.End}
            </span>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Org Spend</p>
            <p className="text-2xl font-bold text-blue-700">
              {costs ? formatCurrency(costs.total, costs.unit) : '--'}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Running Notebooks</p>
            <p className="text-2xl font-bold text-green-700">{runningNotebooks}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Live Endpoints</p>
            <p className="text-2xl font-bold text-purple-700">{liveEndpoints}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Live Studio Apps</p>
            <p className="text-2xl font-bold text-amber-700">{liveApps}</p>
          </div>
        </div>

        {/* Per-account breakdown */}
        <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
          Cost by Account
        </h4>
        {!costs && loading && <p className="text-gray-500 text-sm">Loading cost data...</p>}
        {costs && (!costs.accounts || costs.accounts.length === 0) && (
          <p className="text-gray-500 text-sm">No SageMaker costs found for this period.</p>
        )}
        {costs && costs.accounts && costs.accounts.length > 0 && (
          <div className="space-y-3">
            {costs.accounts.map((acc) => (
              <div key={acc.accountId} className="flex items-center">
                <div className="w-32 sm:w-40 flex-shrink-0 text-sm text-gray-700 truncate" title={`${acc.accountName} (${acc.accountId})`}>
                  {acc.accountName}
                </div>
                <div className="flex-grow bg-gray-100 rounded-full h-5 mx-2 overflow-hidden">
                  <div
                    className="bg-blue-500 h-5 rounded-full"
                    style={{ width: `${maxCost > 0 ? Math.max((acc.amount / maxCost) * 100, 2) : 0}%` }}
                  />
                </div>
                <div className="w-24 flex-shrink-0 text-right text-sm font-medium text-gray-800">
                  {formatCurrency(acc.amount, acc.unit)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Account filter */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center space-x-3">
          <label htmlFor="sm-account-filter" className="text-sm font-medium text-gray-700">
            Filter by account:
          </label>
          <select
            id="sm-account-filter"
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All accounts</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Notebook instances control panel */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Notebook Instances
          <span className="ml-2 text-sm text-gray-500 font-normal">({filteredNotebooks.length})</span>
        </h3>
        {filteredNotebooks.length === 0 ? (
          <p className="text-gray-500 text-sm">{loading ? 'Loading...' : 'No notebook instances found.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Region</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredNotebooks.map((nb) => {
                  const key = `nb:${nb.accountId}:${nb.name}`;
                  const busy = actionInProgress[key];
                  const canStop = (nb.status || '').toLowerCase() === 'inservice';
                  return (
                    <tr key={key} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 font-medium text-gray-800">{nb.name}</td>
                      <td className="py-2 pr-4 text-gray-600">{nb.accountName}</td>
                      <td className="py-2 pr-4 text-gray-600">{nb.instanceType || '-'}</td>
                      <td className="py-2 pr-4 text-gray-600">{nb.region}</td>
                      <td className="py-2 pr-4"><StatusBadge status={nb.status} /></td>
                      <td className="py-2 pr-4">
                        <button
                          onClick={() => onStopNotebook(nb.name, nb.accountId, nb.region)}
                          disabled={!canStop || busy}
                          className="bg-red-500 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-1 px-3 rounded text-xs"
                          title={canStop ? 'Stop notebook instance' : 'Only InService notebooks can be stopped'}
                        >
                          {busy ? 'Stopping...' : 'Stop'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Endpoints control panel */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">
          Endpoints
          <span className="ml-2 text-sm text-gray-500 font-normal">({filteredEndpoints.length})</span>
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Real-time endpoints cannot be paused. Deleting an endpoint stops its billing.
        </p>
        {filteredEndpoints.length === 0 ? (
          <p className="text-gray-500 text-sm">{loading ? 'Loading...' : 'No endpoints found.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">Region</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEndpoints.map((ep) => {
                  const key = `ep:${ep.accountId}:${ep.name}`;
                  const busy = actionInProgress[key];
                  const status = (ep.status || '').toLowerCase();
                  const canDelete = !['deleting', 'deleted'].includes(status);
                  return (
                    <tr key={key} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 font-medium text-gray-800">{ep.name}</td>
                      <td className="py-2 pr-4 text-gray-600">{ep.accountName}</td>
                      <td className="py-2 pr-4 text-gray-600">{ep.region}</td>
                      <td className="py-2 pr-4"><StatusBadge status={ep.status} /></td>
                      <td className="py-2 pr-4 text-gray-600">{formatDate(ep.creationTime)}</td>
                      <td className="py-2 pr-4">
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete endpoint "${ep.name}" (${ep.region}) in ${ep.accountName}? This cannot be undone.`)) {
                              onDeleteEndpoint(ep.name, ep.accountId, ep.region);
                            }
                          }}
                          disabled={!canDelete || busy}
                          className="bg-red-500 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-1 px-3 rounded text-xs"
                          title="Delete endpoint to stop billing"
                        >
                          {busy ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Studio apps control panel */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">
          Studio Apps
          <span className="ml-2 text-sm text-gray-500 font-normal">({filteredApps.length})</span>
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Studio apps (KernelGateway/JupyterServer) cannot be paused. Deleting an app stops its compute billing; Studio data in the user/space is retained.
        </p>
        {filteredApps.length === 0 ? (
          <p className="text-gray-500 text-sm">{loading ? 'Loading...' : 'No Studio apps found.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">App</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Owner</th>
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">Region</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map((app) => {
                  const key = `app:${app.accountId}:${app.region}:${app.appType}:${app.name}`;
                  const busy = actionInProgress[`app:${app.accountId}:${app.region}:${app.name}`];
                  const status = (app.status || '').toLowerCase();
                  const canDelete = !['deleting', 'deleted'].includes(status);
                  const owner = app.spaceName || app.userProfileName || '-';
                  return (
                    <tr key={key} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 font-medium text-gray-800">{app.name}</td>
                      <td className="py-2 pr-4 text-gray-600">{app.appType}</td>
                      <td className="py-2 pr-4 text-gray-600">{owner}</td>
                      <td className="py-2 pr-4 text-gray-600">{app.accountName}</td>
                      <td className="py-2 pr-4 text-gray-600">{app.region}</td>
                      <td className="py-2 pr-4"><StatusBadge status={app.status} /></td>
                      <td className="py-2 pr-4">
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete app "${app.name}" (${app.appType}, ${app.region}) in ${app.accountName}? This stops its compute.`)) {
                              onDeleteApp(app);
                            }
                          }}
                          disabled={!canDelete || busy}
                          className="bg-red-500 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-1 px-3 rounded text-xs"
                          title="Delete Studio app to stop compute billing"
                        >
                          {busy ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SageMakerTab;
